/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import { Component, HostListener, OnInit } from '@angular/core';
import { AnalyticsService } from './common/services/analytics.service';
import { SeoService } from './common/services/seo.service';
import { TranslateService } from '@ngx-translate/core';
import { Languages } from './common/models/languages';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { filter } from 'rxjs/operators';
import { EventMessage, EventType } from '@azure/msal-browser';
import { LocaleService } from './common/services/locale.service';
import { BaseComponent } from './common/classes/base-component';
import { strings as englishStrings } from 'ngx-timeago/language-strings/en';
import { strings as deutschStrings } from 'ngx-timeago/language-strings/de';
import { TimeagoIntl } from 'ngx-timeago';

@Component({
  selector: 'app-component',
  template: '<router-outlet></router-outlet>',
})
export class AppComponent extends BaseComponent implements OnInit {
  @HostListener('window:resize', ['$event']) onResize() {
    this.setVhVar();
  }

  constructor(
    private analytics: AnalyticsService,
    private seoService: SeoService,
    private translateService: TranslateService,
    private msalBroadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private localeService: LocaleService,
    private intl: TimeagoIntl
  ) {
    super();
    this.handleLoginFailureError();
    this.localeService.initLocale(
      navigator.language.includes(Languages.EN) ? Languages.EN : Languages.GE
    );
    this.setLang(this.localeService.currentLocale);
  }

  ngOnInit(): void {
    this.setVhVar();
    this.analytics.trackPageViews();
    this.seoService.trackCanonicalChanges();
  }

  setVhVar() {
    // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
    const vh = window.innerHeight * 0.01;
    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  // todo remove to the separate helper
  setLang(lang: string) {
    switch (lang) {
      case 'en':
        this.intl.strings = englishStrings;
        this.intl.strings.seconds = '%d seconds';
        this.intl.strings.second = '%d second';
        break;
      case 'de':
        this.intl.strings = deutschStrings;
        this.intl.strings.seconds = '%d Sekunden';
        this.intl.strings.second = '%d Sekunde';
        break;
      default:
        break;
    }
    this.intl.changes.next();
  }

  /*
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/4378
   *
   * When we click cancel on the forgot password form, we are redirected to the application and receive an error
   * AADB2C90091, while the expected behavior is to be redirected to the login form.
   *
   * To fix this behavior we just need to reload the page, and we will be redirected to the login form.
   *
   * Another option to solve the problem is to create a login error page in the application that is not protected by
   * guard and enter its address in the configuration in the loginFailedRoute field
   * in this block src/app/auth-config.ts:30
   */
  private handleLoginFailureError(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => {
          return (
            msg.eventType === EventType.LOGIN_FAILURE ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
          );
        })
      )
      .subscribe((result: EventMessage) => {
        if (result.error && result.error.message.indexOf('AADB2C90091') > -1) {
          window.location.reload();
        }
      });
  }
}
